/**
 * @fileoverview Componente responsável por iniciar novas conversas manualmente.
 */

import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  ListItemText,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

import { X, Copy, MessageCircle } from "lucide-react";
const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      background: theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(18, 18, 19, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)"
        : "#ffffff",
      border: theme.palette.type === "dark" ? "1px solid rgba(91, 124, 255, 0.2)" : "none",
      borderRadius: "20px",
      boxShadow: theme.palette.type === "dark"
        ? "0 8px 32px rgba(0, 0, 0, 0.4)"
        : "0 8px 32px rgba(0, 0, 0, 0.1)",
      minWidth: 340,
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, rgba(91, 124, 255, 0.1) 0%, rgba(157, 124, 255, 0.1) 100%)"
      : "linear-gradient(135deg, rgba(61, 92, 253, 0.05) 0%, rgba(138, 35, 135, 0.05) 100%)",
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(91, 124, 255, 0.2)"
      : "1px solid rgba(0, 0, 0, 0.08)",
    padding: "12px 16px",
  },
  titleText: {
    color: theme.palette.type === "dark" ? "#ffffff" : "#1a1a1a",
    fontWeight: 600,
    fontSize: "1.1rem",
  },
  phoneNumber: {
    textAlign: "center",
    padding: theme.spacing(2),
    background: theme.palette.type === "dark"
      ? "rgba(91, 124, 255, 0.1)"
      : "rgba(0, 0, 0, 0.04)",
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
  phoneText: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#ffffff" : "#1a1a1a",
    fontFamily: "monospace",
  },
  online: {
    fontSize: 11,
    color: "#25d366",
  },
  offline: {
    fontSize: 11,
    color: "#e1306c",
  },
  selectWrapper: {
    marginTop: theme.spacing(1),
  },
  selectLabel: {
    fontSize: "0.85rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
    marginBottom: 6,
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    padding: theme.spacing(2),
  },
  startButton: {
    background: "linear-gradient(135deg, #25d366 0%, #128C7E 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #128C7E 0%, #075E54 100%)",
    },
  },
  copyButton: {
    borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.23)",
  },
}));

const StartConversationModal = ({ open, onClose, phoneNumber }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);

  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [loading, setLoading] = useState(false);

  // Conexões ativas (CONNECTED)
  const connectedWhatsapps = whatsApps.filter(w => w.status === "CONNECTED");

  useEffect(() => {
    // Auto-selecionar apenas se houver uma única conexão disponível
    if (open && connectedWhatsapps.length === 1) {
      setSelectedWhatsapp(connectedWhatsapps[0].id);
    }
  }, [open, connectedWhatsapps]);

  const handleClose = () => {
    setSelectedWhatsapp("");
    setLoading(false);
    onClose();
  };

  // DDDs onde o WhatsApp NÃO usa o 9º dígito (mesmo que a ANATEL exija)
  // Fonte: backend/src/services/WbotServices/CheckNumber.ts
  const DDD_WITHOUT_NINE = [
    // Minas Gerais
    "31", "32", "33", "34", "35", "37", "38",
    // Paraná
    "41", "42", "43", "44", "45", "46",
    // Santa Catarina
    "47", "48", "49",
    // Rio Grande do Sul
    "51", "53", "54", "55",
    // Centro-Oeste
    "61", "62", "63", "64", "65", "66", "67",
    // Norte
    "68", "69", "91", "92", "93", "94", "95", "96", "97",
    // Nordeste
    "71", "73", "74", "75", "77", "79", "81", "82", "83", "84", "85", "86", "87", "88", "89", "98", "99"
  ];

  const formatPhoneForApi = (phone) => {
    // Remove tudo que não for número
    let cleaned = phone.replace(/\D/g, "");

    // Se começar com 0, remove
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }

    // Se não tiver código do país (55), adiciona
    if (cleaned.length <= 11) {
      cleaned = "55" + cleaned;
    }

    // Remove o 9º dígito para DDDs que o WhatsApp não usa
    // Ex: 5551999999999 (13 dígitos) → 555199999999 (12 dígitos)
    if (cleaned.length === 13 && cleaned.startsWith("55")) {
      const ddd = cleaned.substring(2, 4);
      const firstDigitAfterDDD = cleaned.charAt(4);

      if (firstDigitAfterDDD === "9" && DDD_WITHOUT_NINE.includes(ddd)) {
        cleaned = cleaned.substring(0, 4) + cleaned.substring(5);
      }
    }

    return cleaned;
  };

  const handleStartConversation = async () => {
    if (!selectedWhatsapp) {
      toast.error("Selecione uma conexão");
      return;
    }

    setLoading(true);

    try {
      const formattedNumber = formatPhoneForApi(phoneNumber);

      // Primeiro, verificar se o contato já existe ou criar
      let contactId;

      try {
        // Tentar buscar contato existente
        const { data: contacts } = await api.get("/contacts", {
          params: { searchParam: formattedNumber }
        });

        const existingContact = contacts.contacts.find(
          c => c.number === formattedNumber || c.number.includes(formattedNumber.slice(-9))
        );

        if (existingContact) {
          contactId = existingContact.id;
        } else {
          // Criar novo contato
          const { data: newContact } = await api.post("/contacts", {
            name: formattedNumber,
            number: formattedNumber,
          });
          contactId = newContact.id;
        }
      } catch (err) {
        // Se falhar ao buscar, criar novo
        const { data: newContact } = await api.post("/contacts", {
          name: formattedNumber,
          number: formattedNumber,
        });
        contactId = newContact.id;
      }

      // Criar ticket sem fila (queueId: null)
      const { data: ticket } = await api.post("/tickets", {
        contactId,
        queueId: null,
        whatsappId: selectedWhatsapp,
        userId: user.id,
        status: "open",
      });

      handleClose();
      history.push(`/tickets/${ticket.uuid}`);
      toast.success("Conversa iniciada!");
    } catch (err) {
      console.error("Erro ao iniciar conversa:", err);
      toastError(err);
    }

    setLoading(false);
  };

  const handleCopyNumber = () => {
    navigator.clipboard.writeText(phoneNumber);
    toast.success("Número copiado!");
  };

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog}>
      <Box className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          Iniciar conversa
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <X size={18} />
        </IconButton>
      </Box>

      <DialogContent>
        <Box className={classes.phoneNumber}>
          <Typography className={classes.phoneText}>
            {phoneNumber}
          </Typography>
        </Box>

        {connectedWhatsapps.length === 0 ? (
          <Typography color="error" align="center">
            Nenhuma conexão ativa disponível
          </Typography>
        ) : connectedWhatsapps.length > 1 ? (
          <Box className={classes.selectWrapper}>
            <Typography className={classes.selectLabel}>
              Selecione a conexão:
            </Typography>
            <Select
              fullWidth
              variant="outlined"
              value={selectedWhatsapp}
              onChange={(e) => setSelectedWhatsapp(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Selecione...
              </MenuItem>
              {connectedWhatsapps.map((whatsapp) => (
                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                  <ListItemText
                    primary={
                      <Typography style={{ fontSize: 14 }}>
                        {whatsapp.name}
                        <span className={classes.online}> (Conectado)</span>
                      </Typography>
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </Box>
        ) : (
          <Typography align="center" style={{ fontSize: 14, opacity: 0.7 }}>
            Conexão: <strong>{connectedWhatsapps[0]?.name}</strong>
          </Typography>
        )}
      </DialogContent>

      <DialogActions className={classes.actionButtons}>
        <Button
          variant="outlined"
          onClick={handleCopyNumber}
          startIcon={<Copy />}
          className={classes.copyButton}
        >
          Copiar
        </Button>
        <Button
          variant="contained"
          onClick={handleStartConversation}
          disabled={loading || !selectedWhatsapp || connectedWhatsapps.length === 0}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <MessageCircle />}
          className={classes.startButton}
        >
          {loading ? "Iniciando..." : "Conversar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartConversationModal;
